import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { timeAgo, fmtDate, fmtTime, fmtGeo } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, FlaskConical, Lock, LockOpen, NotebookPen, Search, ShieldAlert, ShieldCheck, MapPin, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import JournalContent from "@/components/journal/JournalContent";
import { Pager, QuickFilters } from "./QuickFilters";

const USER_VIEWS = [["", "All"], ["recent_login", "Recently active"], ["active", "Most active"], ["journalers", "Most entries"], ["new_week", "New this week"], ["never", "Never logged in"], ["locked", "Locked / failed logins"], ["admins", "Admins"], ["google", "Google sign-in"]].map(([key, label]) => ({ key, label }));
const PAGE = 50;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "never");

function Fact({ label, value, testId, warn = false }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3" data-testid={testId}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 truncate text-sm font-semibold ${warn ? "text-destructive" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function RoleDialog({ u, open, onOpenChange, onDone }) {
  const promote = u.role !== "admin";
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setTyped(""); }, [open]);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${u.user_id}/role`, { role: promote ? "admin" : "user", confirm_email: promote ? typed.trim() : undefined });
      toast.success(promote ? `${u.email} now has admin access.` : `${u.email} is a regular user again.`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="role-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl">{promote ? "Grant admin access?" : "Remove admin access?"}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {promote ? (
                <>
                  <p>Admins can edit every question, see every user&apos;s journal, and change other roles. Only grant this to someone you fully trust.</p>
                  <p>Type <span className="font-mono text-foreground">{u.email}</span> to confirm.</p>
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="user's email address" autoComplete="off" data-testid="role-confirm-input" />
                </>
              ) : (
                <p><span className="font-mono text-foreground">{u.email}</span> will lose access to the admin area immediately.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="role-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy || (promote && typed.trim().toLowerCase() !== u.email.toLowerCase())}
            className={`rounded-full ${promote ? "bg-primary text-primary-foreground" : "bg-destructive text-destructive-foreground hover:bg-destructive/90"}`} data-testid="role-confirm">
            {promote ? "Grant admin access" : "Remove admin access"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EntryDialog({ userId, entryId, onOpenChange }) {
  const { data } = useQuery({ queryKey: ["admin-entry", userId, entryId], queryFn: async () => (await api.get(`/admin/users/${userId}/entries/${entryId}`)).data, enabled: !!entryId });
  return (
    <Dialog open={!!entryId} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="admin-entry-dialog">
        {!data ? <div className="h-40 animate-pulse rounded-xl bg-muted" /> : (
          <>
            <DialogHeader className="text-left">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{fmtDate(data.created_at)} · {fmtTime(data.created_at)}{data.updated_at !== data.created_at ? ` · edited ${timeAgo(data.updated_at)}` : ""}</p>
              <DialogTitle className="font-serif text-2xl">{data.title || (data.question ? `On ${data.question.reference}` : "Untitled entry")}</DialogTitle>
              {data.question && <DialogDescription className="no-reftag font-serif text-base italic">{data.question.reference} · “{data.question.question_kjv}”</DialogDescription>}
            </DialogHeader>
            <JournalContent doc={data.doc} className="mt-2 text-[15px]" testId="admin-entry-content" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ u, open, onOpenChange, onDone }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setTyped(""); }, [open]);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.user_id}`, { params: { confirm_email: typed.trim() } });
      toast.success(`${u.email} and all their data were deleted.`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-user-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl">Delete this account?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>This permanently removes the account, its sessions, saved questions, journal entries, highlights, bookmarks, blog posts and login history. It cannot be undone.</p>
              <p>Type <span className="font-mono text-foreground">{u.email}</span> to confirm.</p>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="user's email address" autoComplete="off" data-testid="delete-confirm-input" />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy || typed.trim().toLowerCase() !== u.email.toLowerCase()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm">Delete account</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TestAccountsBanner({ onDone }) {
  const { data, refetch } = useQuery({ queryKey: ["admin-test-accounts"], queryFn: async () => (await api.get("/admin/test-accounts")).data });
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setTyped(""); }, [open]);
  if (!data?.count) return null;
  const purge = async () => {
    setBusy(true);
    try {
      const res = (await api.post("/admin/test-accounts/purge", { confirm: typed.trim() })).data;
      toast.success(`Deleted ${res.deleted} test account${res.deleted === 1 ? "" : "s"} and their data.`);
      setOpen(false);
      refetch();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 px-4 py-3" data-testid="test-accounts-banner">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground"><FlaskConical className="mr-1.5 inline h-4 w-4 text-destructive" />{data.count} test account{data.count === 1 ? "" : "s"} (@example.com, @qgtest.dev, @qg.org)</p>
        <p className="truncate font-mono text-[11px] text-muted-foreground" title={data.users.map((u) => u.email).join(", ")}>{data.users.slice(0, 4).map((u) => u.email).join(", ")}{data.count > 4 ? ` +${data.count - 4} more` : ""}</p>
      </div>
      <Button variant="outline" size="sm" className="rounded-full border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setOpen(true)} data-testid="purge-test-accounts-button"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete all test accounts</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="purge-test-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete {data.count} test account{data.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>Every non-admin account with an <span className="font-mono text-foreground">@example.com</span>, <span className="font-mono text-foreground">@qgtest.dev</span> or <span className="font-mono text-foreground">@qg.org</span> email will be removed along with its sessions, saved questions, journal entries, highlights, bookmarks, blog posts and login history. This cannot be undone.</p>
                <p>Type <span className="font-mono text-foreground">DELETE</span> to confirm.</p>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" autoComplete="off" data-testid="purge-confirm-input" />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); purge(); }} disabled={busy || typed.trim() !== "DELETE"} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="purge-confirm">Delete them all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserDetail({ userId, onBack }) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["admin-user", userId], queryFn: async () => (await api.get(`/admin/users/${userId}`)).data });
  const [roleOpen, setRoleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [openEntry, setOpenEntry] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-user", userId] }); qc.invalidateQueries({ queryKey: ["admin-users"] }); };
  const unlock = async () => {
    try { await api.post(`/admin/users/${userId}/unlock`); toast.success("Account unlocked."); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (isLoading || !data) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;
  const { user: u, stats, entries, favorites, login_attempts: attempts, most_viewed: viewed } = data;

  return (
    <div className="space-y-5" data-testid="admin-user-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-user-back"><ArrowLeft className="h-4 w-4" /> All users</button>
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-5">
        <Avatar className="h-14 w-14 border border-border">
          {u.picture && <AvatarImage src={u.picture} alt={u.name} />}
          <AvatarFallback className="bg-secondary text-lg font-semibold text-secondary-foreground">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-serif text-2xl font-semibold text-foreground" data-testid="admin-user-name">{u.name || "Seeker"}</h2>
          <p className="truncate font-mono text-xs text-muted-foreground" data-testid="admin-user-email">{u.email} · {u.auth_provider} · {u.user_id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {u.locked && <Button variant="outline" size="sm" className="rounded-full border-destructive/50 text-destructive" onClick={unlock} data-testid="admin-user-unlock"><LockOpen className="mr-1 h-3.5 w-3.5" /> Unlock</Button>}
          <span className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase ${u.role === "admin" ? "bg-gold/15 text-gold" : "bg-muted text-muted-foreground"}`} data-testid="admin-user-role">{u.role}</span>
          {u.user_id !== me?.user_id && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setRoleOpen(true)} data-testid="admin-user-role-button">
              <ShieldCheck className="mr-1 h-3.5 w-3.5" /> {u.role === "admin" ? "Remove admin" : "Make admin"}
            </Button>
          )}
          <RoleDialog u={u} open={roleOpen} onOpenChange={setRoleOpen} onDone={refresh} />
          {u.user_id !== me?.user_id && u.role !== "admin" && (
            <Button variant="outline" size="sm" className="rounded-full border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setDeleteOpen(true)} data-testid="admin-user-delete-button">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          )}
          <DeleteDialog u={u} open={deleteOpen} onOpenChange={setDeleteOpen} onDone={() => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-test-accounts"] }); onBack(); }} />
          <EntryDialog userId={userId} entryId={openEntry} onOpenChange={setOpenEntry} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Fact label="Joined" value={fmt(u.created_at)} testId="fact-joined" />
        <Fact label="Last login" value={fmt(u.last_login_at)} testId="fact-last-login" />
        <Fact label="Last login location" value={fmtGeo(u.last_login_geo) || (u.last_login_ip ? `Unknown · ${u.last_login_ip}` : "unknown")} testId="fact-last-location" />
        <Fact label="Logins" value={u.login_count} testId="fact-logins" />
        <Fact label="Last seen" value={stats.last_seen_at ? `${timeAgo(stats.last_seen_at)} · ${stats.last_path}` : "never"} testId="fact-last-seen" />
        <Fact label="Failed attempts (recent / total)" value={`${u.failed_login_count} / ${u.failed_login_total}`} warn={u.failed_login_count > 0} testId="fact-failed" />
        <Fact label="Last failed attempt" value={fmt(u.failed_login_last_at)} testId="fact-failed-last" />
        <Fact label="Account status" value={u.locked ? `Locked until ${fmt(u.locked_until)}` : "Active"} warn={u.locked} testId="fact-status" />
        <Fact label="Lockouts" value={stats.lockouts} testId="fact-lockouts" />
        <Fact label="Page views" value={stats.page_views} testId="fact-views" />
        <Fact label="Active sessions" value={stats.active_sessions} testId="fact-sessions" />
        <Fact label="Journal entries" value={entries.length} testId="fact-entries" />
        <Fact label="Saved questions" value={favorites.length} testId="fact-favorites" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-user-entries">
          <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><NotebookPen className="h-4 w-4 text-gold" /> Journal entries</h3>
          <p className="mt-1 text-xs text-muted-foreground">Click an entry to read it.</p>
          {entries.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No entries yet.</p> : (
            <ul className="mt-3 max-h-[480px] divide-y divide-border overflow-y-auto pr-1">
              {entries.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => setOpenEntry(e.id)} className="flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-muted/50" data-testid={`admin-entry-${e.id}`}>
                    <span className="w-28 shrink-0 font-mono text-[11px] text-foreground">{fmtDate(e.created_at)}</span>
                    <span className="w-16 shrink-0 font-mono text-[11px] text-muted-foreground">{fmtTime(e.created_at)}</span>
                    <span className="no-reftag min-w-0 flex-1 truncate font-mono text-[11px] text-gold">{e.question ? e.question.reference : <span className="text-muted-foreground">—</span>}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="space-y-4">
          <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-user-logins">
            <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><ShieldAlert className="h-4 w-4 text-gold" /> Recent login attempts</h3>
            {attempts.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No attempts recorded yet.</p> : (
              <ul className="mt-3 max-h-56 divide-y divide-border overflow-y-auto font-mono text-xs">
                {attempts.map((a, i) => (
                  <li key={`${a.at}-${a.ip}-${i}`} className="flex items-center gap-3 py-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${a.ok ? "bg-emerald-500" : "bg-destructive"}`} />
                    <span className="w-16 shrink-0">{a.ok ? "ok" : "failed"}</span>
                    <span className="flex-1 truncate text-muted-foreground">{fmt(a.at)} · {a.ip || "?"}{fmtGeo(a.geo) ? <span className="ml-1 inline-flex items-center gap-0.5 text-foreground"><MapPin className="h-3 w-3 text-gold" />{fmtGeo(a.geo)}</span> : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-user-favorites">
            <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><Bookmark className="h-4 w-4 text-gold" /> Saved questions</h3>
            {favorites.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">Nothing saved yet.</p> : (
              <ul className="mt-3 max-h-56 space-y-1.5 overflow-y-auto">
                {favorites.map((f) => (
                  <li key={f.question.id}><Link to={`/question/${f.question.id}`} className="block truncate text-sm text-foreground hover:text-primary"><span className="no-reftag font-mono text-[10px] text-muted-foreground">{f.question.reference}</span> “{f.question.question_kjv}”</Link></li>
                ))}
              </ul>
            )}
          </section>
          {viewed.length > 0 && (
            <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-user-viewed">
              <h3 className="font-serif text-xl font-semibold text-foreground">Most viewed questions</h3>
              <ul className="mt-3 space-y-1.5">
                {viewed.map((v) => (
                  <li key={v.question.id} className="flex items-center gap-2 text-sm"><span className="w-8 shrink-0 font-mono text-xs text-muted-foreground">{v.views}×</span><Link to={`/question/${v.question.id}`} className="truncate text-foreground hover:text-primary">“{v.question.question_kjv}”</Link></li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(0); }, [debounced, view]);
  const { data } = useQuery({ queryKey: ["admin-users", debounced, view, page], queryFn: async () => (await api.get("/admin/users", { params: { search: debounced || undefined, view: view || undefined, skip: page * PAGE, limit: PAGE } })).data });
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  if (selected) return <UserDetail userId={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-4" data-testid="admin-users">
      <TestAccountsBanner onDone={() => qc.invalidateQueries({ queryKey: ["admin-users"] })} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or id" className="pl-9" data-testid="admin-user-search" />
        </div>
        <div className="flex items-center gap-3">
          <p className="font-mono text-xs text-muted-foreground" data-testid="admin-user-count">{data ? `${data.total} account${data.total === 1 ? "" : "s"}${data.total > PAGE ? ` · showing ${page * PAGE + 1}–${Math.min((page + 1) * PAGE, data.total)}` : ""}` : " "}</p>
          <Pager page={page} pages={pages} onChange={setPage} testId="user-pager" />
        </div>
      </div>
      <QuickFilters options={USER_VIEWS} value={view} onChange={setView} testId="user-filter" />
      {!data ? <div className="h-40 animate-pulse rounded-2xl bg-muted" /> : data.users.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">No accounts match.</p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          {data.users.map((u) => (
            <li key={u.user_id}>
              <button type="button" onClick={() => setSelected(u.user_id)} className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50" data-testid={`admin-user-${u.user_id}`}>
                <Avatar className="h-9 w-9 border border-border">
                  {u.picture && <AvatarImage src={u.picture} alt={u.name} />}
                  <AvatarFallback className="bg-secondary text-sm font-semibold text-secondary-foreground">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {u.name || "Seeker"} {u.user_id === me?.user_id && <span className="font-mono text-[10px] text-gold">(you)</span>}
                    {u.role === "admin" && <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 font-mono text-[10px] uppercase text-gold">admin</span>}
                    {u.locked && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 font-mono text-[10px] uppercase text-destructive"><Lock className="h-3 w-3" /> locked</span>}
                  </p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">{u.email} · {u.auth_provider} · joined {timeAgo(u.created_at)} · last login {u.last_login_at ? timeAgo(u.last_login_at) : "never"}</p>
                </div>
                <div className="hidden gap-4 font-mono text-[11px] text-muted-foreground sm:flex">
                  <span title="Journal entries">{u.entries} entr{u.entries === 1 ? "y" : "ies"}</span>
                  <span title="Saved questions">{u.favorites} saved</span>
                  <span title="Logins">{u.login_count} logins</span>
                  <span title="Failed attempts" className={u.failed_login_count ? "text-destructive" : ""}>{u.failed_login_count} failed</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {pages > 1 && <div className="flex justify-end"><Pager page={page} pages={pages} onChange={setPage} testId="user-pager-bottom" /></div>}
    </div>
  );
}
